import { useFormikContext } from "formik";
import CloseIcon from "icons/close";
import UploadIcon from "icons/upload";
import { useEffect, useRef, useState } from "react";
import { Cropper } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { showToastMessage } from "utils";

function AdminV2ImageUpload({ name, label }) {
  const [image, setImage] = useState();
  const [fileName, setFileName] = useState("");
  const [isCropping, setIsCropping] = useState(false);
  const formik = useFormikContext();
  const cropperRef = useRef(null);
  const rafRef = useRef(0);
  const inputRef = useRef(null);
  const hasLocalCropSessionRef = useRef(false);
  const API_URL = process.env.REACT_APP_API_URL || "";
  const uploadsBaseUrl = API_URL.replace(/\/api\/?$/, "").replace(/\/$/, "");

  const resolveImageSrc = (value) => {
    if (!value || typeof value !== "string") return "";
    if (
      value.startsWith("data:") ||
      value.startsWith("blob:") ||
      value.startsWith("http://") ||
      value.startsWith("https://")
    ) {
      return value;
    }

    if (value.startsWith("/uploads/")) {
      return `${uploadsBaseUrl}${value}`;
    }

    if (value.includes("/uploads/")) {
      return `${uploadsBaseUrl}/uploads/${value.split("/uploads/").pop()}`.replace(/([^:]\/)\/+/g, "$1");
    }

    return `${uploadsBaseUrl}/uploads/${value}`;
  };

  useEffect(() => {
    const currentValue = formik.values?.[name];
    const currentName = formik.values?.[`${name}Name`];

    if (currentValue && typeof currentValue === "string" && !hasLocalCropSessionRef.current) {
      setImage(resolveImageSrc(currentValue));
      setFileName(currentName || currentValue.split("/").pop() || "Current image");
      setIsCropping(false);
      return;
    }

    if (!currentValue) {
      hasLocalCropSessionRef.current = false;
      setImage(undefined);
      setFileName("");
      setIsCropping(false);
    }
  }, [formik.values?.[name], formik.values?.[`${name}Name`], name]);

  const setFieldSafely = (dataUrl) => {
    if (!dataUrl) return;
    formik.setFieldValue(name, dataUrl, false);
  };

  const handleImage = (event) => {
    event.preventDefault();

    const file = event.target.files?.[0] || event.dataTransfer?.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      showToastMessage("Please upload a png or jpg image");
      return;
    }

    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > 5) {
      showToastMessage("Please upload an image smaller than 5 MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      hasLocalCropSessionRef.current = true;
      setImage(dataUrl);
      setFileName(file.name);
      setIsCropping(true);
      formik.setFieldValue(name, dataUrl, false);
      formik.setFieldValue(`${name}Name`, file.name, false);
    };
    reader.readAsDataURL(file);
  };

  const handlePickFile = () => {
    inputRef.current?.click();
  };

  const handleEditCurrentImage = () => {
    if (!image) return;
    hasLocalCropSessionRef.current = true;
    setIsCropping(true);
  };

  const handleClose = () => {
    setImage(undefined);
    setFileName("");
    hasLocalCropSessionRef.current = false;
    setIsCropping(false);
    formik.setFieldValue(name, "", false);
    formik.setFieldValue(`${name}Name`, "", false);
    const input = document.getElementById(`admin-v2-${name}`);
    if (input) input.value = "";
  };

  const handleCropLive = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      try {
        const canvas = cropper.getCroppedCanvas();
        if (!canvas) return;
        setFieldSafely(canvas.toDataURL());
      } catch {
        // ignore crop interim errors
      }
    });
  };

  const handleCropEnd = (event) => {
    try {
      const dataUrl = event?.target?.cropper?.getCroppedCanvas()?.toDataURL();
      setFieldSafely(dataUrl);
    } catch {
      // ignore
    }
  };

  return (
    <div className="tw-grow tw-flex tw-flex-col tw-gap-[7px]">
      <div className="tw-text-[#304861] tw-text-[15px] md:tw-text-[13px] tw-font-[500] tw-heading-[20px]">
        {label}
      </div>
      <div className="tw-bg-[#F2F5F5] tw-rounded-[5px] tw-min-h-[55px] md:tw-min-h-[40px] tw-px-[11px] tw-flex tw-items-center">
        <div className="tw-w-full tw-max-w-[80vw]">
          <input
            type="file"
            id={`admin-v2-${name}`}
            ref={inputRef}
            accept="image/png, image/jpeg, image/jpg"
            onChange={handleImage}
            hidden
          />

          {image ? (
            <>
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-1">
                <div className="tw-overflow-hidden">
                  <div className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-[16px] tw-placeholder-gray-300 md:tw-text-[14px] tw-heading-[20px] tw-text-black">
                    {fileName || "Selected image"}
                  </div>
                  <button
                    type="button"
                    onClick={handlePickFile}
                    className="tw-mt-1 tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-xs tw-font-medium tw-text-[#304861]"
                  >
                    <UploadIcon />
                    Change image
                  </button>
                  {!isCropping ? (
                    <button
                      type="button"
                      onClick={handleEditCurrentImage}
                      className="tw-mt-1 tw-ml-3 tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-xs tw-font-medium tw-text-[#304861]"
                    >
                      Edit current
                    </button>
                  ) : null}
                </div>
                <button type="button" onClick={handleClose} className="tw-cursor-pointer">
                  <CloseIcon />
                </button>
              </div>

              <div className="tw-bg-white tw-mb-[10px] tw-rounded-[5px] tw-overflow-hidden">
                {isCropping ? (
                  <>
                    <div className="tw-hidden">
                      <img src={image} alt={`${label} preview`} />
                    </div>
                    <Cropper
                      ref={cropperRef}
                      src={image}
                      aspectRatio={name === "stripImage" ? 1033 / 407 : 1}
                      onReady={(event) => {
                        try {
                          const dataUrl = event.target.cropper.getCroppedCanvas().toDataURL();
                          setFieldSafely(dataUrl);
                        } catch {
                          // ignore
                        }
                      }}
                      crop={handleCropLive}
                      onCrop={handleCropLive}
                      onCropend={handleCropEnd}
                      style={{ width: "100%" }}
                      zoomTo={0.8}
                      autoCropArea={0.9}
                    />
                  </>
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-center tw-bg-[#F7F9FC] tw-p-3">
                    <img
                      src={resolveImageSrc(image)}
                      alt={`${label} current`}
                      className={`tw-h-full tw-w-full tw-rounded-[4px] tw-object-contain ${
                        name === "stripImage" ? "tw-max-h-[180px]" : "tw-max-h-[320px]"
                      }`}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={handlePickFile}
              className="tw-cursor-pointer tw-h-[40px] tw-flex tw-items-center tw-text-[#BEBEBE] tw-justify-between tw-w-full"
            >
              <div className="tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-[16px] tw-placeholder-gray-300 md:tw-text-[14px] tw-heading-[20px] tw-text-[#BEBEBE]">
                .png or .jpeg fields only
              </div>
              <UploadIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminV2ImageUpload;
